/*******************************************************************************************
   _____            __              ____   ____                         .__               
  /  _  \   _______/  |________  ___\   \ /   /____   ____  __ __  _____|__|____    ____  
 /  /_\  \ /  ___/\   __\_  __ \/  _ \   Y   // __ \ /    \|  |  \/  ___/  \__  \  /    \ 
/    |    \\___ \  |  |  |  | \(  <_> )     /\  ___/|   |  \  |  /\___ \|  |/ __ \|   |  \
\____|__  /____  > |__|  |__|   \____/ \___/  \___  >___|  /____//____  >__(____  /___|  /
        \/     \/                                 \/     \/           \/        \/     \/ 
********************************************************************************************
Alert Disclaimer
********************************************************************************************

Author:     Nicholas Hamilton, PhD
Email:      nicholasehamilton@gmail.com
Date:       13th February 2024

*******************************************************************************************/
import React                            from 'react';
import { AlertSessionDismissible }      from 'components';
import { useDevice }                    from 'contexts';
import { withTranslation }              from './hoc';

const DEFAULT_DURATION          = 7 * 24 * 60 * 60 * 1000;  // 7 Days
const DEFAULT_CHECK_FREQUENCY   = 5 * 1000;                 // 5 Seconds        
const DEFAULT_SEVERITY          = "info";                   // Severity
const DEFAULT_ID                = "alertDisclaimer";        // ID

/**
 * A component that displays an alert disclaimer with customizable settings. 
 * The alert can be dismissed and will stay dismissed for a specified duration, 
 * and it can be checked periodically for whether it should remain visible.
 *
 * @component
 * @example
 * // Example usage of AlertDisclaimer
 * <AlertDisclaimer 
 *   id="customAlert" 
 *   duration={604800000} // 7 days
 *   checkFrequency={5000} // check every 5 seconds
 *   severity="warning" 
 * />
 *
 * @param {Object} props - The component props.
 * @param {string} props.id - The unique identifier for the alert, used to store the dismissal state. Defaults to 'alertDisclaimer'.
 * @param {number} props.duration - The duration (in milliseconds) for which the alert will stay dismissed. Defaults to 7 days.
 * @param {number} props.checkFrequency - The frequency (in milliseconds) at which the alert dismissal state will be checked. Defaults to 5000 ms (5 seconds).
 * @param {string} props.severity - The severity level of the alert (e.g., "info", "warning", "error"). Defaults to "info".
 * 
 * @returns {JSX.Element|null} The rendered component or null if the alert is dismissed or the user is not active.
 */
export const AlertDisclaimer = withTranslation(({ 
    t, 
    id                  = DEFAULT_ID, 
    duration            = DEFAULT_DURATION,
    checkFrequency      = DEFAULT_CHECK_FREQUENCY,
    severity            = DEFAULT_SEVERITY
}) => {
    
    // Is Native App
    const { isNative }  = useDevice();

    // Short-circuit rendering of the alert on native app
    if (isNative) 
        return null;

    // Render AlertSessionDismissible with the necessary props
    return (
        <AlertSessionDismissible 
            id              = { id } 
            duration        = { duration } 
            checkFrequency  = { checkFrequency }
            severity        = { severity }
        >
            <strong>{t('components.alertDisclaimer.salulation')}:</strong> {t('components.alertDisclaimer.message')}
        </AlertSessionDismissible>
    );
});

export default AlertDisclaimer;